import { IBaseTraitNode } from '../types/talentNodeTypes';

/**
 * Some talent choice nodes are PURELY a player preference: they change only how a spell is
 * cast/targeted (cosmetic), not its power. Treating them as real choices splits otherwise-identical
 * builds apart, lists them in the "differences between builds" UI, and can name them as kill-speed
 * "talent shifts". This module lets us whitelist such nodes so they count as a non-choice node.
 *
 * Identification is by SPELL ID: chosen nodes always carry `node.entries[].spell.id`, so this works
 * in both the backend (stats generation) and the browser (build-diff UI) with no game-data lookup.
 * This file must stay browser-safe — it may only import from shared types.
 *
 * To add a node: decode a real loadout (or read the RaiderIO talent tooltip) and record the spell id
 * of EVERY selectable entry of the node (see the match rule on `isPreferenceChoiceNode`). Only add
 * nodes whose variants are genuinely interchangeable in power.
 */
export const PREFERENCE_CHOICE_ENTRIES: ReadonlyArray<{ spellId: number; note: string }> = [
  // The four ground-targeted AoE spells that gained a "cast at cursor" choice node in 12.0. Each is a
  // 2-entry choice node whose entries are the SAME ability (verified by name in DBC 12.0.0–12.0.5),
  // differing only in targeting — purely a player preference. Spell ids are stable across 12.0.x.
  // Elemental Shaman — Earthquake (choice node 80985)
  { spellId: 462620, note: 'Earthquake (Elemental Shaman) targeting variant — node 80985' },
  { spellId: 61882, note: 'Earthquake (Elemental Shaman) targeting variant — node 80985' },
  // Destruction Warlock — Rain of Fire (choice node 72069)
  { spellId: 5740, note: 'Rain of Fire (Destruction Warlock) targeting variant — node 72069' },
  { spellId: 1214467, note: 'Rain of Fire (Destruction Warlock) targeting variant — node 72069' },
  // Mage — Blizzard (choice node 108864)
  { spellId: 190356, note: 'Blizzard (Mage) targeting variant — node 108864' },
  { spellId: 1248829, note: 'Blizzard (Mage) targeting variant — node 108864' },
  // Mage — Flamestrike (choice node 109409)
  { spellId: 2120, note: 'Flamestrike (Mage) targeting variant — node 109409' },
  { spellId: 1254851, note: 'Flamestrike (Mage) targeting variant — node 109409' }
];

const PREFERENCE_CHOICE_SPELL_IDS: ReadonlySet<number> = new Set(PREFERENCE_CHOICE_ENTRIES.map((e) => e.spellId));

/** The single bucket every preference-node selection collapses to for grouping/diffing. */
export const PREFERENCE_CHOICE_CANONICAL_ENTRY_INDEX = 0;

/**
 * A node is a "preference" node only when it is a choice node (2+ entries) AND *every* selectable
 * entry's spell is whitelisted. Requiring all entries is a fail-safe: if Blizzard later adds a
 * meaningful entry to the node, it stops collapsing (reverts to a real choice) instead of silently
 * hiding a real decision.
 */
export function isPreferenceChoiceNode(node: IBaseTraitNode | undefined | null): boolean {
  if (!node || node.entries.length < 2) {
    return false;
  }
  return node.entries.every((entry) => entry.spell?.id != null && PREFERENCE_CHOICE_SPELL_IDS.has(entry.spell.id));
}

/**
 * Returns the entryIndex to use for build grouping / diffing. Preference nodes collapse to a single
 * canonical bucket so every variant compares equal; all other nodes are returned unchanged.
 */
export function canonicalEntryIndexForBuild(
  node: IBaseTraitNode | undefined | null,
  entryIndex: number | undefined
): number | undefined {
  if (entryIndex == null) {
    return entryIndex;
  }
  return node && isPreferenceChoiceNode(node) ? PREFERENCE_CHOICE_CANONICAL_ENTRY_INDEX : entryIndex;
}
